import type { TrailPoint, BladeKey } from './GameState.ts';
import type { RemoteViewLayout } from 'shared/lib/tv-game/nativeLayout.ts';
import type { NativeComboPayload } from 'shared/lib/tv-game/nativeFrame.ts';
import type { KeypointType } from 'shared/lib/tv-game/pose.ts';

export type LegKickPhase = 'lobby' | 'countdown' | 'calibration' | 'playing' | 'gameover';

export type LegKickCalibNormPoint = { nx: number; ny: number };
export type LegKickCalibNormMap = Map<KeypointType, LegKickCalibNormPoint>;

/** Processed target for canvas (normalized 0–1 in full game rect). */
export interface LegKickTargetRow {
  nx: number;
  ny: number;
  /** Radius as fraction of game height (same as native). */
  radius: number;
  fillColor: string;
  lifetimeProgress: number;
  hitAnimationProgress: number;
  arcadeSequenceNumber: number;
  showArcadeNumber: boolean;
  isHit: boolean;
}

export interface LegKickLabelRow {
  nx: number;
  ny: number;
  text: string;
  color: string;
  alpha: number;
}

export interface LegKickDisplayState {
  phase: LegKickPhase;
  bladeTrails: Map<BladeKey, TrailPoint[]>;
  gameStartTime: number;
  remoteView: RemoteViewLayout | null;
  leftView: RemoteViewLayout | null;
  rightView: RemoteViewLayout | null;
  splitTwoPlayers: boolean;
  gameMode: 'CLASSIC' | 'ARCADE' | 'ZEN' | null;
  score: number;
  player1Score: number | null;
  player2Score: number | null;
  streak: number;
  lives: number | null;
  timeLeft: number | null;
  combo: NativeComboPayload | null;
  isPaused: boolean;
  isGameOver: boolean;
  calibNormPoints: LegKickCalibNormMap;
  calibNormPointsP2: LegKickCalibNormMap;
  calibSliced: boolean;
  calibSlicedP2: boolean;
  targets: LegKickTargetRow[];
  labels: LegKickLabelRow[];
  currentLevel: number | null;
  /** Progress within current level (0…1); TV bar fill width = this value. */
  levelProgress: number | null;
  /** 0…1 from native `detectionProgress` (Target Attack). */
  detectionProgress: number | null;
  /** Blade-trail key prefix for player 1 (e.g. 'slot1' or actual playerId). */
  p1PlayerId: string | null;
  /** Blade-trail key prefix for player 2 in DUO mode; null in solo. */
  p2PlayerId: string | null;
}

export function createLegKickInitialState(): LegKickDisplayState {
  return {
    phase: 'lobby',
    bladeTrails: new Map(),
    gameStartTime: 0,
    remoteView: null,
    leftView: null,
    rightView: null,
    splitTwoPlayers: false,
    gameMode: null,
    score: 0,
    player1Score: null,
    player2Score: null,
    streak: 0,
    lives: null,
    timeLeft: null,
    combo: null,
    isPaused: false,
    isGameOver: false,
    calibNormPoints: new Map(),
    calibNormPointsP2: new Map(),
    calibSliced: false,
    calibSlicedP2: false,
    targets: [],
    labels: [],
    currentLevel: null,
    levelProgress: null,
    detectionProgress: null,
    p1PlayerId: null,
    p2PlayerId: null,
  };
}
